import { useState, useRef, useEffect, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { api, apiErr } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { timeAgo } from "@/lib/format";
import {
  Bot, Send, Loader2, ShieldAlert, CheckCircle2, XCircle, Wrench, Sparkles, Trash2, AlertTriangle,
  Plus, Star, MessageSquare, Folder, History, Clock,
} from "lucide-react";

const CONTEXTS = [
  { id: "general", label: "Général" },
  { id: "infrastructure", label: "Infrastructure" },
  { id: "drone", label: "Drone" },
  { id: "mission", label: "Mission" },
  { id: "replay", label: "Replay" },
];
const ctxLabel = (id) => CONTEXTS.find((c) => c.id === id)?.label || id;

const RISK_META = {
  low: { color: "#22C55E", label: "FAIBLE" },
  medium: { color: "#3B82F6", label: "MOYEN" },
  high: { color: "#F59E0B", label: "ÉLEVÉ" },
  critical: { color: "#EF4444", label: "CRITIQUE" },
};
const riskMeta = (r) => RISK_META[r] || RISK_META.low;

export default function AIAssistant() {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [context, setContext] = useState("general");
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [actions, setActions] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [busyAction, setBusyAction] = useState("");
  const [sessions, setSessions] = useState([]);
  const [favOnly, setFavOnly] = useState(false);
  const [verifs, setVerifs] = useState([]);
  const scrollRef = useRef(null);
  const consumedRef = useRef(false);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, loading, verifs]);

  const loadSessions = useCallback(async () => {
    try {
      const { data } = await api.get("/ai/sessions");
      setSessions(data.sessions || []);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { loadSessions(); }, [loadSessions]);

  const newConversation = () => {
    setSessionId(null); setMessages([]); setActions([]); setInput("");
  };

  const sendText = useCallback(async (text, ctx) => {
    if (!text || !text.trim()) return;
    const useCtx = ctx || context;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: text }]);
    setLoading(true);
    try {
      const { data } = await api.post("/ai/chat", { message: text, session_id: sessionId, context: useCtx });
      setSessionId(data.session_id);
      setMessages((m) => [...m, { role: "assistant", content: data.reply, tool_calls: data.tool_calls || [] }]);
      if (data.proposed_actions?.length) {
        setActions((a) => [...data.proposed_actions.map((x) => ({ ...x })), ...a]);
        toast.message(`${data.proposed_actions.length} action(s) proposée(s) — validation requise`);
      }
      loadSessions();
    } catch (e) {
      setMessages((m) => [...m, { role: "assistant", content: `⚠️ ${apiErr(e, "Assistant indisponible")}`, error: true }]);
    } finally {
      setLoading(false);
    }
  }, [context, sessionId, loadSessions]);

  const send = () => sendText(input, context);

  // Réception d'un diagnostic (ou autre) via navigation -> analyse automatique lecture seule.
  useEffect(() => {
    const st = location.state;
    if (st?.prefill && !consumedRef.current) {
      consumedRef.current = true;
      const ctx = st.context || "drone";
      setContext(ctx);
      setSessionId(null); setMessages([]); setActions([]);
      navigate(location.pathname, { replace: true, state: null });
      if (st.autoSend) {
        setTimeout(() => sendText(st.prefill, ctx), 50);
      } else {
        setInput(st.prefill);
      }
    }
  }, [location, navigate, sendText]);

  const loadSession = async (sid) => {
    if (loading) return;
    try {
      const { data } = await api.get(`/ai/sessions/${sid}/messages`);
      setSessionId(sid);
      setContext(data.session?.context || "general");
      setMessages((data.messages || []).map((m) => ({
        role: m.role, content: m.content, tool_calls: m.tool_calls || [], error: false,
      })));
      const acts = (data.actions || []).slice().reverse();
      setActions(acts);
    } catch (e) { toast.error(apiErr(e, "Session introuvable")); }
  };

  const toggleFav = async (e, s) => {
    e.stopPropagation();
    const next = !s.favorite;
    setSessions((list) => list.map((x) => (x.id === s.id ? { ...x, favorite: next } : x)));
    try { await api.patch(`/ai/sessions/${s.id}`, { favorite: next }); }
    catch { loadSessions(); }
  };

  const deleteSession = async (e, s) => {
    e.stopPropagation();
    try {
      await api.delete(`/ai/sessions/${s.id}`);
      setSessions((list) => list.filter((x) => x.id !== s.id));
      if (s.id === sessionId) newConversation();
      toast.message("Session supprimée");
    } catch (err) { toast.error(apiErr(err, "Suppression impossible")); }
  };

  const updateAction = (id, patch) => setActions((a) => a.map((x) => (x.id === id ? { ...x, ...patch } : x)));

  // ---- Vérification automatique post-exécution (30 s) ----
  const verifPrompt = (act) => {
    const a = act.args || {};
    const target = a.drone_id || a.service || a.container || a.path || "";
    let hint = "utilise l'outil de lecture le plus pertinent pour confirmer l'effet";
    const t = act.tool;
    if (t === "restart_service") hint = "vérifie l'état des services avec get_services_status";
    else if (t === "restart_drone_service") hint = `relance run_drone_diagnostic pour ${a.drone_id || "le drone"}`;
    else if (t === "send_drone_command") hint = `relance run_drone_diagnostic (ou get_drone_telemetry) pour ${a.drone_id || "le drone"}`;
    else if (t === "switch_drone_network") hint = `relance get_drone_network pour ${a.drone_id || "le drone"} et indique le transport actif réel, le signal et l'état WireGuard`;
    else if (t === "execute_agent_update") hint = "re-vérifie la version de l'agent avec check_agent_version";
    else if (t === "write_file" || t === "propose_file_update") hint = "relis le fichier avec read_file";
    else if (act.category === "docker") hint = "vérifie l'état avec docker_diagnose";
    return `[Vérification automatique] L'action « ${t} »${target ? ` (cible : ${target})` : ""} a été exécutée il y a 30 secondes. Vérifie qu'elle a bien pris effet : ${hint}. Donne un verdict concis (OK ou problème détecté).`;
  };

  const runVerification = useCallback(async (v) => {
    try {
      const { data } = await api.post("/ai/chat", { message: v.prompt, session_id: v.sessionId, context: v.context });
      setMessages((m) => [...m, { role: "assistant", content: data.reply, tool_calls: data.tool_calls || [], verify: true }]);
      if (data.proposed_actions?.length) setActions((a) => [...data.proposed_actions.map((x) => ({ ...x })), ...a]);
      loadSessions();
    } catch (e) {
      setMessages((m) => [...m, { role: "assistant", content: `⚠️ Vérification impossible : ${apiErr(e)}`, error: true }]);
    } finally {
      setVerifs((list) => list.filter((x) => x.id !== v.id));
    }
  }, [loadSessions]);

  // Tick du compte à rebours
  useEffect(() => {
    const t = setInterval(() => {
      setVerifs((list) => (list.some((v) => v.phase === "counting")
        ? list.map((v) => (v.phase === "counting" ? { ...v, secondsLeft: v.secondsLeft - 1 } : v))
        : list));
    }, 1000);
    return () => clearInterval(t);
  }, []);

  // Déclenche la vérification quand le compte à rebours arrive à 0
  useEffect(() => {
    const due = verifs.filter((v) => v.phase === "counting" && v.secondsLeft <= 0);
    if (!due.length) return;
    setVerifs((list) => list.map((v) => (due.find((d) => d.id === v.id) ? { ...v, phase: "running" } : v)));
    due.forEach((v) => runVerification(v));
  }, [verifs, runVerification]);

  const scheduleVerification = (act) => {
    setVerifs((list) => [...list, {
      id: `${act.id}-${Date.now()}`, sessionId, context,
      tool: act.tool, prompt: verifPrompt(act), secondsLeft: 30, phase: "counting",
    }]);
  };

  const confirm = async (act) => {
    setBusyAction(act.id);
    try {
      const { data } = await api.post(`/ai/actions/${act.id}/confirm`);
      if (data.status === "pending") {
        updateAction(act.id, { confirmations: data.confirmations });
        toast.warning(`Action critique : confirmez à nouveau (${data.confirmations}/${data.needed}).`);
      } else if (data.status === "executed") {
        updateAction(act.id, { status: "executed", result_summary: data.result_summary });
        toast.success("Action exécutée");
        const summary = (data.result_summary || "").slice(0, 280);
        setMessages((m) => [...m, {
          role: "assistant", confirmed: true,
          content: `Action « ${act.tool} » confirmée et exécutée avec succès.${summary ? `\n${summary}` : ""}`,
        }]);
        scheduleVerification(act);
      } else {
        updateAction(act.id, { status: "failed", error: data.error });
        toast.error(data.error || "Action échouée");
        setMessages((m) => [...m, {
          role: "assistant", error: true,
          content: `⚠️ Action « ${act.tool} » échouée : ${data.error || "erreur inconnue"}`,
        }]);
      }
    } catch (e) {
      toast.error(apiErr(e, "Confirmation refusée"));
    } finally {
      setBusyAction("");
    }
  };

  const cancel = async (act) => {
    setBusyAction(act.id);
    try {
      await api.post(`/ai/actions/${act.id}/cancel`);
      updateAction(act.id, { status: "cancelled" });
      toast.message("Action annulée");
    } catch (e) {
      toast.error(apiErr(e, "Annulation impossible"));
    } finally {
      setBusyAction("");
    }
  };

  const pending = actions.filter((a) => a.status === "pending");
  const shownSessions = favOnly ? sessions.filter((s) => s.favorite) : sessions;

  return (
    <div className="p-5 h-full flex flex-col" data-testid="ai-assistant-page">
      <div className="flex items-start gap-3 flex-wrap mb-4">
        <div className="w-9 h-9 border border-white/15 rounded-sm flex items-center justify-center"><Sparkles size={18} className="text-[#3B82F6]" /></div>
        <div>
          <h1 className="font-chivo text-2xl font-black tracking-tighter uppercase leading-none">Assistant IA</h1>
          <p className="font-mono text-[11px] text-[#A3A3A3] mt-1">Analyse, diagnostic et actions contrôlées — chaque action sensible nécessite votre validation.</p>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <select data-testid="ai-context-select" value={context} onChange={(e) => { setContext(e.target.value); newConversation(); }}
            className="bg-[#141414] border border-white/15 rounded-sm font-mono text-[10px] px-2 py-2 text-white">
            {CONTEXTS.map((c) => <option key={c.id} value={c.id}>Contexte : {c.label}</option>)}
          </select>
        </div>
      </div>

      <div className="flex-1 grid grid-cols-1 lg:grid-cols-4 gap-4 min-h-0">
        {/* Sessions sidebar */}
        <div className="hidden lg:flex flex-col bg-[#141414] border border-white/10 rounded-sm min-h-0">
          <div className="px-3 py-2.5 border-b border-white/10 flex items-center gap-2">
            <History size={14} className="text-[#3B82F6]" />
            <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-[#A3A3A3]">Sessions</span>
            <button data-testid="ai-new-session" onClick={newConversation} title="Nouvelle session"
              className="ml-auto w-6 h-6 flex items-center justify-center border border-white/15 rounded-sm hover:bg-white/10"><Plus size={13} /></button>
          </div>
          <div className="flex items-center gap-1 px-2 py-2 border-b border-white/10">
            <button data-testid="ai-filter-all" onClick={() => setFavOnly(false)}
              className={`flex-1 flex items-center justify-center gap-1 font-mono text-[9px] uppercase tracking-wider rounded-sm py-1.5 transition-colors ${!favOnly ? "bg-[#3B82F6] text-white" : "text-[#525252] border border-white/10 hover:text-white"}`}>
              <Folder size={11} /> Toutes
            </button>
            <button data-testid="ai-filter-fav" onClick={() => setFavOnly(true)}
              className={`flex-1 flex items-center justify-center gap-1 font-mono text-[9px] uppercase tracking-wider rounded-sm py-1.5 transition-colors ${favOnly ? "bg-[#F59E0B] text-black" : "text-[#525252] border border-white/10 hover:text-white"}`}>
              <Star size={11} /> Favoris
            </button>
          </div>
          <div className="flex-1 overflow-y-auto p-2 space-y-1" data-testid="ai-session-list">
            {shownSessions.length === 0 && (
              <div className="font-mono text-[10px] text-[#525252] p-2">{favOnly ? "Aucune session favorite." : "Aucune session. Démarrez une conversation."}</div>
            )}
            {shownSessions.map((s) => (
              <div key={s.id} data-testid={`ai-session-${s.id}`} onClick={() => loadSession(s.id)}
                className={`group cursor-pointer rounded-sm border px-2 py-2 transition-colors ${s.id === sessionId ? "border-[#3B82F6]/60 bg-[#3B82F6]/10" : "border-white/10 hover:bg-white/[0.04]"}`}>
                <div className="flex items-start gap-1.5">
                  <MessageSquare size={11} className="text-[#525252] mt-0.5 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="font-mono text-[11px] text-white truncate">{s.title || "Sans titre"}</div>
                    <div className="font-mono text-[8px] text-[#525252] uppercase tracking-wider mt-0.5">{ctxLabel(s.context)} · {timeAgo(s.updated_at)}</div>
                  </div>
                  <button data-testid={`ai-session-fav-${s.id}`} onClick={(e) => toggleFav(e, s)} title="Favori"
                    className="shrink-0 opacity-60 hover:opacity-100">
                    <Star size={12} fill={s.favorite ? "#F59E0B" : "none"} color={s.favorite ? "#F59E0B" : "#737373"} />
                  </button>
                  <button data-testid={`ai-session-delete-${s.id}`} onClick={(e) => deleteSession(e, s)} title="Supprimer"
                    className="shrink-0 opacity-0 group-hover:opacity-70 hover:!opacity-100 text-[#EF4444]">
                    <Trash2 size={12} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Chat */}
        <div className="lg:col-span-2 flex flex-col bg-[#141414] border border-white/10 rounded-sm min-h-0">
          <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="ai-messages">
            {messages.length === 0 && (
              <div className="h-full flex flex-col items-center justify-center text-center gap-2 text-[#525252]">
                <Bot size={32} />
                <div className="font-mono text-[12px]">Posez une question sur l'infrastructure, les drones ou les missions.</div>
                <div className="font-mono text-[10px]">Ex. « Quel est l'état de santé du serveur ? »</div>
              </div>
            )}
            {messages.map((m, i) => (
              <div key={i} data-testid={`ai-message-${i}`} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[85%] rounded-sm px-3 py-2 font-mono text-[12px] whitespace-pre-wrap break-words ${
                  m.role === "user" ? "bg-[#3B82F6]/20 border border-[#3B82F6]/30 text-white"
                  : m.error ? "bg-[#EF4444]/10 border border-[#EF4444]/30 text-[#EF4444]"
                  : m.confirmed ? "bg-[#22C55E]/10 border border-[#22C55E]/30 text-[#86EFAC]"
                  : m.verify ? "bg-[#3B82F6]/10 border border-[#3B82F6]/25 text-[#BFDBFE]"
                  : "bg-[#0A0A0A] border border-white/10 text-[#E5E5E5]"}`}>
                  {m.role === "assistant" && (
                    <div className="flex items-center gap-1.5 mb-1" style={{ color: m.confirmed ? "#22C55E" : m.verify ? "#60A5FA" : m.error ? "#EF4444" : "#3B82F6" }}>
                      {m.confirmed ? <CheckCircle2 size={12} /> : m.verify ? <Clock size={12} /> : <Bot size={12} />}
                      <span className="font-mono text-[9px] uppercase tracking-wider">{m.confirmed ? "Action exécutée" : m.verify ? "Vérification auto" : "SkyCore IA"}</span>
                    </div>
                  )}
                  <div data-testid={m.role === "assistant" ? "ai-reply" : undefined}>{m.content}</div>
                  {m.tool_calls?.length > 0 && (
                    <div className="mt-2 pt-2 border-t border-white/10 space-y-0.5">
                      {m.tool_calls.map((t, j) => (
                        <div key={j} className="flex items-center gap-1.5 font-mono text-[9px] text-[#525252]">
                          <Wrench size={9} /> <span className="text-[#A3A3A3]">{t.tool}</span>
                          <span style={{ color: t.ok ? "#22C55E" : "#EF4444" }}>{t.ok ? "ok" : "échec"}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}
            {verifs.map((v) => (
              <div key={v.id} data-testid={`ai-verify-${v.id}`} className="flex justify-start">
                <div className="max-w-[85%] rounded-sm px-3 py-2 font-mono text-[11px] bg-[#3B82F6]/10 border border-[#3B82F6]/30 text-[#93C5FD] flex items-center gap-2">
                  {v.phase === "counting" ? (
                    <><Clock size={13} className="shrink-0" /><span data-testid={`ai-verify-countdown-${v.id}`}>Vérification automatique de « {v.tool} » dans {Math.max(0, v.secondsLeft)} s…</span></>
                  ) : (
                    <><Loader2 size={13} className="animate-spin shrink-0" /> Vérification de « {v.tool} » en cours…</>
                  )}
                </div>
              </div>
            ))}
            {loading && <div className="flex items-center gap-2 font-mono text-[11px] text-[#525252]"><Loader2 size={13} className="animate-spin" /> L'assistant analyse…</div>}
          </div>
          <div className="border-t border-white/10 p-3 flex items-center gap-2">
            <input data-testid="ai-input" value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()}
              placeholder="Écrivez votre demande…" disabled={loading}
              className="flex-1 bg-[#0A0A0A] border border-white/10 rounded-sm font-mono text-[12px] px-3 py-2.5 text-white outline-none disabled:opacity-50" />
            <button data-testid="ai-send" onClick={send} disabled={loading || !input.trim()}
              className="flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-wider bg-[#3B82F6] rounded-sm px-4 py-2.5 hover:bg-[#3B82F6]/85 disabled:opacity-40">
              {loading ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} Envoyer
            </button>
          </div>
        </div>

        {/* Actions proposées + historique */}
        <div className="flex flex-col bg-[#141414] border border-white/10 rounded-sm min-h-0">
          <div className="px-3 py-2.5 border-b border-white/10 flex items-center gap-2">
            <ShieldAlert size={14} className="text-[#F59E0B]" />
            <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-[#A3A3A3]">Actions proposées</span>
            {pending.length > 0 && <span className="ml-auto font-mono text-[9px] bg-[#F59E0B]/20 text-[#F59E0B] rounded px-1.5">{pending.length}</span>}
          </div>
          <div className="flex-1 overflow-y-auto p-3 space-y-2" data-testid="ai-proposed-actions">
            {actions.length === 0 && <div className="font-mono text-[11px] text-[#525252]">Aucune action proposée. L'IA proposera ici les actions sensibles à valider.</div>}
            {actions.map((a) => {
              const m = riskMeta(a.risk_level);
              const done = a.status !== "pending";
              return (
                <div key={a.id} data-testid={`ai-action-${a.id}`} className="bg-[#0A0A0A] border border-white/10 rounded-sm p-2.5 space-y-2">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Wrench size={12} className="text-[#3B82F6]" />
                    <span className="font-mono text-[11px] text-white">{a.tool}</span>
                    <span className="font-mono text-[8px] uppercase font-bold rounded px-1.5 py-0.5"
                      style={{ color: m.color, backgroundColor: `${m.color}1A`, border: `1px solid ${m.color}55` }}>{m.label}</span>
                    {a.requires_double_confirmation && <span className="font-mono text-[8px] uppercase text-[#EF4444] border border-[#EF4444]/40 rounded px-1">double</span>}
                    <span className="ml-auto font-mono text-[8px] uppercase tracking-wider"
                      style={{ color: a.status === "executed" ? "#22C55E" : a.status === "failed" ? "#EF4444" : a.status === "cancelled" ? "#525252" : "#F59E0B" }}>{a.status}</span>
                  </div>
                  {a.args && Object.keys(a.args).length > 0 && (
                    <div className="font-mono text-[9px] text-[#A3A3A3] break-words">{Object.entries(a.args).map(([k, v]) => `${k}=${String(v).slice(0, 80)}`).join("  ·  ")}</div>
                  )}
                  <div className="font-mono text-[9px] text-[#737373] flex items-start gap-1"><AlertTriangle size={10} className="mt-0.5 shrink-0 text-[#F59E0B]" />{a.impact}</div>
                  {done && (a.result_summary || a.error) && (
                    <pre className="font-mono text-[9px] text-[#A3A3A3] bg-[#141414] border border-white/10 rounded-sm p-1.5 whitespace-pre-wrap break-words max-h-28 overflow-y-auto">{a.error || a.result_summary}</pre>
                  )}
                  {!done && (
                    <div className="flex items-center gap-1.5">
                      <button data-testid={`ai-action-confirm-${a.id}`} onClick={() => confirm(a)} disabled={busyAction === a.id}
                        className="flex items-center gap-1 font-mono text-[9px] uppercase tracking-wider rounded-sm px-2 py-1 disabled:opacity-40"
                        style={{ backgroundColor: a.requires_double_confirmation ? "#EF4444" : "#3B82F6" }}>
                        <CheckCircle2 size={11} /> {a.requires_double_confirmation && (a.confirmations || 0) >= 1 ? "Confirmer (final)" : "Confirmer"}
                      </button>
                      <button data-testid={`ai-action-cancel-${a.id}`} onClick={() => cancel(a)} disabled={busyAction === a.id}
                        className="flex items-center gap-1 font-mono text-[9px] uppercase tracking-wider border border-white/15 rounded-sm px-2 py-1 hover:bg-white/10 disabled:opacity-40">
                        <XCircle size={11} /> Annuler
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          <div className="px-3 py-2 border-t border-white/10 font-mono text-[9px] text-[#525252]">
            Rôle : <span className="text-[#A3A3A3] uppercase">{user?.role}</span> · L'IA ne peut jamais exécuter une action sans votre confirmation.
          </div>
        </div>
      </div>
    </div>
  );
}
